import {FC, useState} from 'react';
import {useParams} from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  AddGeoConfigDialog,
  formatOnboardingType,
  OnboardingDetails,
  OnboardingGeoConfig,
  OnboardingType,
  useGetOnboarding,
  useRemoveOnboardingGeoConfig,
  useUpdateOnboarding,
} from '@entities/onboarding';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {UtmConfigs} from './UtmConfigs';

import {COLOR__LIGHT_BACK, COLOR__WHITE} from '@/theme/colors';

const geoConfigColumns: MRT_ColumnDef<OnboardingGeoConfig>[] = [
  {
    accessorKey: 'countryCode',
    header: 'Country code',
    size: 140,
    enableSorting: false,
  },
  {
    accessorKey: 'config',
    header: 'Config',
    enableSorting: false,
    Cell: ({row}) => (
      <Link
        href={row.original.config}
        target="_blank"
        rel="noopener noreferrer"
      >
        {row.original.config}
      </Link>
    ),
  },
];

// Pretty-prints the JSON file behind `config` (falls back to raw text) so an
// admin can sanity-check it without leaving the page.
const ConfigContentPreview: FC<{configUrl: string}> = ({configUrl}) => {
  const {
    data: content,
    isPending,
    error,
  } = useQuery({
    queryKey: ['onboarding-config-content', configUrl],
    queryFn: async () => {
      const response = await fetch(configUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.text();
    },
    retry: false,
  });

  if (isPending) {
    return <CircularProgress size={16} />;
  }

  if (error) {
    return (
      <Typography color="error">
        Could not load file content. It may be blocked by CORS — use "Open link"
        to view it directly.
      </Typography>
    );
  }

  let formatted = content ?? '';
  try {
    formatted = JSON.stringify(JSON.parse(content ?? ''), null, 2);
  } catch {
    //
  }

  return (
    <Box
      component="pre"
      m={0}
      p={1.5}
      bgcolor={COLOR__LIGHT_BACK}
      borderRadius="6px"
      maxHeight={400}
      sx={{
        fontSize: 12,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {formatted || '—'}
    </Box>
  );
};

// Remounted (via `key`) whenever the onboarding type changes, so its local
// state doesn't leak between types when navigating without a full page reload.
const OnboardingDetailsContent: FC<{onboarding: OnboardingDetails}> = ({
  onboarding,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const [configInput, setConfigInput] = useState(onboarding.config);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const {mutate: updateOnboarding, isPending: isSaving} = useUpdateOnboarding();
  const {mutate: removeGeoConfig} = useRemoveOnboardingGeoConfig();

  const trimmedConfigInput = configInput.trim();

  const handleSaveConfig = () => {
    if (!trimmedConfigInput || trimmedConfigInput === onboarding.config) {
      return;
    }

    updateOnboarding(
      {type: onboarding.type, config: trimmedConfigInput},
      {
        onSuccess: () => {
          enqueueSnackbar('Config updated successfully', {
            variant: 'success',
          });
        },
        onError: err => {
          const msg = isAxiosError(err) ? getErrorMessageFromApiError(err) : '';
          enqueueSnackbar(`Could not update config. ${msg}`, {
            variant: 'error',
          });
        },
      },
    );
  };

  const handleDeleteGeoConfig = async (geoConfig: OnboardingGeoConfig) => {
    try {
      await openConfirmation({
        title: 'Remove geo config',
        content: `Remove the ${geoConfig.countryCode} config?`,
        okButtonText: 'Remove',
      });
    } catch {
      return;
    }

    removeGeoConfig(
      {type: onboarding.type, countryCode: geoConfig.countryCode},
      {
        onSuccess: () => {
          enqueueSnackbar('Geo config removed successfully', {
            variant: 'success',
          });
        },
        onError: err => {
          const msg = isAxiosError(err) ? getErrorMessageFromApiError(err) : '';
          enqueueSnackbar(`Could not remove geo config. ${msg}`, {
            variant: 'error',
          });
        },
      },
    );
  };

  return (
    <>
      <CardBox bgcolor={COLOR__WHITE} mb={3}>
        <Typography color="text.secondary" mb={0.5}>
          Config link
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            size="small"
            fullWidth
            value={configInput}
            onChange={e => setConfigInput(e.target.value)}
          />
          <Button
            onClick={handleSaveConfig}
            disabled={
              isSaving ||
              !trimmedConfigInput ||
              trimmedConfigInput === onboarding.config
            }
          >
            Save
          </Button>
          <Tooltip title="Open link">
            <span>
              <IconButton
                size="small"
                component="a"
                href={onboarding.config}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box mt={2}>
          <Typography color="text.secondary" mb={0.5}>
            Content
          </Typography>
          <ConfigContentPreview configUrl={onboarding.config} />
        </Box>
      </CardBox>

      <UtmConfigs type={onboarding.type} configs={onboarding.extDataConfigs} />

      <CardBox bgcolor={COLOR__WHITE}>
        <Header
          title="Geo configs"
          mb={2}
          isBackButtonShown={false}
          rightPart={
            <Button onClick={() => setIsAddDialogOpen(true)}>
              Add geo config
            </Button>
          }
        />

        <MrTable
          columns={geoConfigColumns}
          data={onboarding.geoConfigs}
          renderRowActions={({row}) => (
            <TableRowActionsContainer>
              <IconButton
                size="small"
                onClick={() => void handleDeleteGeoConfig(row.original)}
              >
                <DeleteIcon />
              </IconButton>
            </TableRowActionsContainer>
          )}
          {...entityTableCommonProps}
        />
      </CardBox>

      <AddGeoConfigDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        type={onboarding.type}
      />
    </>
  );
};

export const OnboardingDetailsPage = () => {
  const {type} = useParams<{type: OnboardingType}>();
  const {data: onboarding, isPending, error} = useGetOnboarding(type);

  if (!type) {
    return 'Invalid onboarding type';
  }

  return (
    <>
      <Header title={formatOnboardingType(type)} mb={1} />

      <BaseWidgetLayout isPending={isPending} error={error}>
        {onboarding && (
          <OnboardingDetailsContent
            key={onboarding.type}
            onboarding={onboarding}
          />
        )}
      </BaseWidgetLayout>
    </>
  );
};
